// Oturum durumunun tek kaynağı. Login/signup/logout akışlarını yönetir ve
// token geçersiz olduğunda (forceLogout) state'i temizleyip login'e yönlendirir.
//
// AuthProvider <Router> İÇİNDE render edilmeli (useNavigate gerektirir).

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import * as authApi from '../features/auth/authApi';
import {
  saveTokens,
  saveEmail,
  clearTokens,
  getEmail,
  hasSession,
} from '../api/tokenStorage';
import { registerLogoutHandler, resetSession } from '../api/sessionManager';
import { looksLikeEmail } from '../utils/validators';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(hasSession());
  const [email, setEmail] = useState(getEmail());

  // forceLogout (interceptor kaynaklı) tetiklenince React state'ini temizle
  // ve login'e yönlendir.
  useEffect(() => {
    registerLogoutHandler(() => {
      setIsAuthenticated(false);
      setEmail(null);
      navigate('/login', { replace: true });
    });
  }, [navigate]);

  const login = useCallback(async (emailOrPhone, password) => {
    const data = await authApi.login({ emailOrPhone, password });
    saveTokens({ access: data.accessToken, refresh: data.refreshToken });
    if (looksLikeEmail(emailOrPhone)) {
      saveEmail(emailOrPhone);
      setEmail(emailOrPhone);
    }
    setIsAuthenticated(true);
    return data;
  }, []);

  const signup = useCallback(async ({ email: emailInput, fullName, password }) => {
    const data = await authApi.register({
      email: emailInput,
      fullName,
      password,
    });
    // Register yanıtı access token'ı `token` alanında döner.
    saveTokens({ access: data.token, refresh: data.refreshToken });
    saveEmail(emailInput);
    setEmail(emailInput);
    setIsAuthenticated(true);
    return data;
  }, []);

  const logout = useCallback(() => {
    clearTokens();
    resetSession();
    setIsAuthenticated(false);
    setEmail(null);
    navigate('/login', { replace: true });
  }, [navigate]);

  const value = {
    isAuthenticated,
    email,
    login,
    signup,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};
